import { Package, Path, Timer, Warning, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { fmtInt } from "@/lib/format";

const Delta = ({ value, goodWhenDown, unit = "" }) => {
  if (value === null || value === undefined || value === 0) {
    return <span className="font-mono text-[11px] text-[#9CA3AF]">no change</span>;
  }
  const up = value > 0;
  const bad = goodWhenDown ? up : false;
  const good = goodWhenDown ? !up : false;
  const tone = bad ? "text-red-600" : good ? "text-emerald-600" : "text-[#64748B]";
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono text-[11px] ${tone}`}>
      <Icon size={11} weight="bold" />
      {up ? "+" : ""}{fmtInt(value)}{unit} vs prev
    </span>
  );
};

const NoPrev = () => <span className="font-mono text-[11px] text-[#9CA3AF]">no prior snapshot</span>;

const Card = ({ label, value, sub, icon: Icon, tone, testid, delta, onClick }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className="group relative col-span-6 border border-[#E3E7EC] bg-[#FFFFFF] p-5 text-left transition-colors hover:bg-[#EDEFF3] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F0323E] lg:col-span-3"
  >
    <div className="flex items-start justify-between">
      <span className="pf-label">{label}</span>
      <Icon size={18} weight="duotone" className={tone} />
    </div>
    <div className="mt-4 font-mono text-4xl font-semibold tracking-tighter text-[#0F172A]">{value}</div>
    <div className="mt-1 text-xs text-[#64748B]">{sub}</div>
    <div className="mt-2">{delta}</div>
    <div className="absolute inset-x-0 bottom-0 h-[2px] scale-x-0 bg-[#F0323E] transition-transform duration-300 group-hover:scale-x-100" />
  </button>
);

export const KpiCards = ({ summary, delta, onNavigate }) => {
  const s = summary || {};
  const d = delta || null;
  const go = (t) => () => onNavigate?.(t);
  return (
    <div className="grid grid-cols-12 gap-px bg-[#E3E7EC]">
      <Card
        testid="kpi-open-orders"
        label="Open Order Lines"
        value={fmtInt(s.total_orders)}
        sub="Unprocessed lines · view by buyer"
        icon={Package}
        tone="text-[#F0323E]"
        delta={d ? <Delta value={d.total_orders} /> : <NoPrev />}
        onClick={go("buyer-tiles")}
      />
      <Card
        testid="kpi-ref2"
        label="Reference 2 Groups"
        value={fmtInt(s.distinct_ref2)}
        sub="Distinct REF2 · open the order lines"
        icon={Path}
        tone="text-[#8B5CF6]"
        delta={d ? <Delta value={d.distinct_ref2} /> : <NoPrev />}
        onClick={go("orders-table")}
      />
      <Card
        testid="kpi-turnaround"
        label="Avg Turnaround"
        value={<span>{fmtInt(s.avg_turnaround)}<span className="ml-1 text-lg text-[#9CA3AF]">d</span></span>}
        sub="Mean days open · by buyer"
        icon={Timer}
        tone="text-amber-500"
        delta={d ? <Delta value={d.avg_turnaround} goodWhenDown unit="d" /> : <NoPrev />}
        onClick={go("turnaround-leaderboard")}
      />
      <Card
        testid="kpi-stuck"
        label="Aging > 30 Days"
        value={fmtInt(s.stuck)}
        sub="Stuck beyond a month · see alerts"
        icon={Warning}
        tone="text-red-500"
        delta={d ? <Delta value={d.stuck} goodWhenDown /> : <NoPrev />}
        onClick={go("alerts-table")}
      />
    </div>
  );
};
